import * as THREE from "three";
import { SCENERY, type SceneryItem, ZONE } from "@mucki/shared";

const COLORS = {
  ground: 0x4f7c3a,
  path: 0xa8946b,
  trunk: 0x5b4032,
  foliage: 0x2f6b3a,
  rock: 0x7c7a76,
  grass: 0x74a850,
  shrineStone: 0xbdb6a6,
  shrineCrystal: 0x7ad7f0,
};

/** Places one instance of an instanced mesh from a scenery item. */
function placeInstances(
  mesh: THREE.InstancedMesh,
  items: SceneryItem[],
  yOffset: (item: SceneryItem) => number,
): void {
  const matrix = new THREE.Matrix4();
  const quaternion = new THREE.Quaternion();
  const position = new THREE.Vector3();
  const scale = new THREE.Vector3();

  items.forEach((item, index) => {
    position.set(item.x, ZONE.groundHeight + yOffset(item), item.z);
    quaternion.setFromEuler(new THREE.Euler(0, item.rotation, 0));
    scale.setScalar(item.scale);
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(index, matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
}

function instanced(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  items: SceneryItem[],
  yOffset: (item: SceneryItem) => number,
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geometry, material, items.length);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  placeInstances(mesh, items, yOffset);
  return mesh;
}

function buildShrine(): THREE.Group {
  const shrine = new THREE.Group();

  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(3, 3.4, 0.6, 12),
    new THREE.MeshStandardMaterial({ color: COLORS.shrineStone, roughness: 0.9 }),
  );
  base.position.y = ZONE.groundHeight + 0.3;
  base.receiveShadow = true;
  shrine.add(base);

  const pillar = new THREE.Mesh(
    new THREE.CylinderGeometry(0.7, 0.9, 3.2, 8),
    new THREE.MeshStandardMaterial({ color: COLORS.shrineStone, roughness: 0.8 }),
  );
  pillar.position.y = ZONE.groundHeight + 2.2;
  pillar.castShadow = true;
  shrine.add(pillar);

  const crystal = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.9),
    new THREE.MeshStandardMaterial({
      color: COLORS.shrineCrystal,
      emissive: COLORS.shrineCrystal,
      emissiveIntensity: 0.6,
      roughness: 0.2,
    }),
  );
  crystal.position.y = ZONE.groundHeight + 4.6;
  crystal.castShadow = true;
  crystal.name = "shrineCrystal";
  shrine.add(crystal);

  // Cat ears on the pillar, so the shrine reads as a cat shrine.
  const earMaterial = new THREE.MeshStandardMaterial({ color: COLORS.shrineStone });
  for (const side of [-1, 1]) {
    const ear = new THREE.Mesh(new THREE.ConeGeometry(0.35, 0.8, 4), earMaterial);
    ear.position.set(side * 0.5, ZONE.groundHeight + 4.0, 0);
    ear.castShadow = true;
    shrine.add(ear);
  }

  return shrine;
}

export interface Zone {
  group: THREE.Group;
  /** Animated in the render loop so the clearing has a sign of life. */
  update: (elapsed: number) => void;
}

export function buildZone(scene: THREE.Scene): Zone {
  scene.fog = new THREE.Fog(0x8fbcd4, 45, 130);

  const group = new THREE.Group();

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(ZONE.size, ZONE.size),
    new THREE.MeshStandardMaterial({ color: COLORS.ground, roughness: 1 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = ZONE.groundHeight;
  ground.receiveShadow = true;
  group.add(ground);

  const path = new THREE.Mesh(
    new THREE.PlaneGeometry(ZONE.pathHalfWidth * 2, ZONE.size),
    new THREE.MeshStandardMaterial({ color: COLORS.path, roughness: 1 }),
  );
  path.rotation.x = -Math.PI / 2;
  path.position.y = ZONE.groundHeight + 0.01;
  path.receiveShadow = true;
  group.add(path);

  const trunkGeometry = new THREE.CylinderGeometry(0.28, 0.4, 3, 6);
  const foliageGeometry = new THREE.ConeGeometry(1.7, 4, 7);
  const trunkMaterial = new THREE.MeshStandardMaterial({ color: COLORS.trunk, roughness: 1 });
  const foliageMaterial = new THREE.MeshStandardMaterial({ color: COLORS.foliage, roughness: 0.95 });

  group.add(instanced(trunkGeometry, trunkMaterial, SCENERY.trees, (item) => 1.5 * item.scale));
  group.add(instanced(foliageGeometry, foliageMaterial, SCENERY.trees, (item) => 4.4 * item.scale));

  group.add(
    instanced(
      new THREE.DodecahedronGeometry(0.6),
      new THREE.MeshStandardMaterial({ color: COLORS.rock, roughness: 1 }),
      SCENERY.rocks,
      (item) => 0.35 * item.scale,
    ),
  );

  group.add(
    instanced(
      new THREE.ConeGeometry(0.22, 0.7, 4),
      new THREE.MeshStandardMaterial({ color: COLORS.grass, roughness: 1 }),
      SCENERY.grass,
      (item) => 0.35 * item.scale,
    ),
  );

  const shrine = buildShrine();
  group.add(shrine);
  scene.add(group);

  const crystal = shrine.getObjectByName("shrineCrystal");

  return {
    group,
    update: (elapsed) => {
      if (!crystal) return;
      crystal.rotation.y = elapsed * 0.6;
      crystal.position.y = ZONE.groundHeight + 4.6 + Math.sin(elapsed * 1.5) * 0.15;
    },
  };
}
